import { useState, useEffect, useMemo, useCallback } from "react";
import moment from "moment";
import { getDataWithComp, getAlert } from "../../../../utils/SamuiUtils";
import { SUMMARY_FIELDS, DETAIL_FIELDS } from "./Fields";

export default function useTopBuyingProducts({
  startDate,
  endDate,
  search,
  sortField,
  sortOrder,
  page,
  perPage,
  statusFilter,
  customerType,
  currencyFilter,
}) {
  const [rawData, setRawData] = useState([]);
  const [rawDataPO_H, setRawDataPO_H] = useState([]);
  const [detailData, setDetailData] = useState([]);
  const [detailInfo, setDetailInfo] = useState({ index: 0, code: "", name: "" });

  useEffect(() => {
    // ดึงข้อมูลจาก API_0202_PO_D
    getDataWithComp("API_0202_PO_D", "Order By Item_Code DESC")
      .then(setRawData)
      .catch((e) => getAlert("FAILED", e.message));
      
    // ดึงข้อมูลจาก API_0201_PO_H เพื่อเอาข้อมูลสกุลเงิน
    getDataWithComp("API_0201_PO_H", "Order By Doc_No DESC")
      .then(setRawDataPO_H)
      .catch((e) => getAlert("FAILED", e.message));
  }, []);
  const byDate = useMemo(
    () => {
      // รวมข้อมูลจาก PO_D และ PO_H
      const mergedData = rawData.map(item => {
        const poHeader = rawDataPO_H.find(h => h.Doc_No === item.Doc_No);
        return {
          ...item,
          Doc_Currency: poHeader?.Doc_Currency || item.Doc_Currency,
          DocStatus_Name: poHeader?.DocStatus_Name || item.DocStatus_Name
        };
      });
      
      return mergedData.filter((i) => {
        const d = moment(i.Doc_Date).format("YYYY-MM-DD");
        return d >= startDate && d <= endDate;
      });
    },
    [rawData, rawDataPO_H, startDate, endDate]
  );
  const grouped = useMemo(() => {
    const m = {};
    byDate.forEach((item) => {      const key = item.Item_Code;
      if (!key) return;
      if (!item.Item_Name) item.Item_Name = key;
      const ap = item.AP_Code;
      const docNo = item.Doc_No; // เพิ่มตัวแปรสำหรับเก็บเลขที่เอกสาร
      
      if (!m[key]) {
        m[key] = {
          ...item,
          ...SUMMARY_FIELDS.reduce((acc, f) => {
            if (f.numeric) acc[f.key] = +item[f.key] || 0;
            return acc;
          }, {}),
          TotalQty: +item.Item_Qty || 0,
          Item_Total: +item.Item_Total || 0,          _customerCounts: { [ap]: 1 },
          _customersSet: new Set([ap]),
          _docNumbers: new Set([docNo]), // เก็บเลขที่เอกสารที่ไม่ซ้ำกัน
        };
      } else {
        SUMMARY_FIELDS.forEach((f) => {
          if (f.numeric) m[key][f.key] += +item[f.key] || 0;
        });
        m[key].TotalQty += +item.Item_Qty || 0;
        m[key].Item_Total += +item.Item_Total || 0;        // นับจำนวนครั้งที่ซื้อจากเลขที่เอกสารที่ไม่ซ้ำกัน
        m[key]._docNumbers.add(docNo);
        m[key]._customerCounts[ap] = (m[key]._customerCounts[ap] || 0) + 1;
        m[key]._customersSet.add(ap);
      }
    });    return Object.values(m).map((entry) => {
      const total_supplier = entry._customersSet.size;
      const total_supplier_repeat = Object.values(entry._customerCounts).filter(
        (c) => c > 1
      ).length;
      const repeat_rate = total_supplier
        ? parseFloat(((total_supplier_repeat / total_supplier) * 100).toFixed(2))
        : 0;
      
      // ใช้จำนวนเอกสารที่ไม่ซ้ำกันเป็นจำนวนครั้งที่ซื้อ
      const purchaseCount = entry._docNumbers.size;
        
      const { _customerCounts, _customersSet, _docNumbers, ...rest } = entry;
      return { 
        ...rest, 
        total_supplier, 
        total_supplier_repeat, 
        repeat_rate, 
        purchaseCount
      };
    });
  }, [byDate]);const filtered = useMemo(() => {
    let a = statusFilter
      ? grouped.filter(i => i.DocStatus_Name === statusFilter)
      : grouped;

    // 🔍 filter ตามประเทศ
    if (customerType === "ในประเทศ") {
      a = a.filter((i) => i.Doc_No?.trim().startsWith("POL"));
    } else if (customerType === "นอกประเทศ") {
      a = a.filter((i) => i.Doc_No?.trim().startsWith("POM"));
    }

    // 💰 filter ตามสกุลเงิน
    if (currencyFilter && currencyFilter !== "ทั้งหมด") {
      a = a.filter((i) => i.Doc_Currency === currencyFilter);
    }

    if (search) {
      const q = search.toLowerCase();
      a = a.filter(i => Object.values(i).some(v => v?.toString().toLowerCase().includes(q)));
    }
    return a;
  }, [grouped, statusFilter, customerType, currencyFilter, search]);

  const sorted = useMemo(() => {
    if (!sortField) return filtered;
    return [...filtered].sort((a, b) => {
      const va = a[sortField] ?? "";
      const vb = b[sortField] ?? "";
      if (!isNaN(+va) && !isNaN(+vb)) return sortOrder === "asc" ? va - vb : vb - va;
      return sortOrder === "asc"
        ? va.toString().localeCompare(vb)
        : vb.toString().localeCompare(va);
    });
  }, [filtered, sortField, sortOrder]);

  const total = sorted.length;
  const pages = perPage > 0 ? Math.ceil(total / perPage) : 1;
  const current = useMemo(
    () => perPage > 0 ? sorted.slice((page - 1) * perPage, page * perPage) : sorted,
    [sorted, page, perPage]
  );
  const sums = useMemo(() => ({
    sumItem_Total: filtered.reduce((s, i) => s + (+i.Item_Total || 0), 0),
    sumPurchaseCount: filtered.reduce((s, i) => s + (i.purchaseCount || 0), 0),
    sumTotalSupplierRepeat: filtered.reduce((s, i) => s + (i.total_supplier_repeat || 0), 0),
  }), [filtered]);
  const showDetailByIndex = useCallback(
    (idx) => {
      const prod = current[idx];
      if (!prod) return;
      const filter = [
        `AND Item_Code LIKE '%${prod.Item_Code}%'`,
        `AND Doc_Date >= '${startDate}'`,
        `AND Doc_Date <= '${endDate}'`,
        "ORDER BY Doc_Date",
      ].join(" ");
      getDataWithComp("API_0202_PO_D", filter)
        .then((list) => {
          setDetailData(list || []);
          setDetailInfo({ index: idx, code: prod.Item_Code, name: prod.Item_Name });
        })
        .catch((e) => getAlert("FAILED", e.message));
    }, [current, startDate, endDate]
  );
  const currencyOptions = useMemo(() => {
    const uniqueCurrencies = new Set();
    uniqueCurrencies.add("ทั้งหมด");
    rawDataPO_H.forEach((item) => {
      if (item.Doc_Currency) {
        uniqueCurrencies.add(item.Doc_Currency);
      }
    });
    return Array.from(uniqueCurrencies).map((currency) => ({
      value: currency,
      label: currency,
    }));
  }, [rawDataPO_H]);

  return { current, total, pages, sums, detailData, detailInfo, currencyOptions, showDetailByIndex };
}
